import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import {
  listNews,
  getNewsPost,
  createNewsPost,
  updateNewsPost,
  deleteNewsPost,
  reorderNews,
  type NewsPostFull,
  type MailReward,
} from "@/lib/api";
import {
  NEWS_LANGUAGES,
  NEWS_ID_PATTERN,
  validateNewsIndex,
  validateNewsPost,
  validateNewsRewards,
  type NewsIndex,
  type NewsIndexEntry,
  type NewsLanguage,
  type NewsIssue,
} from "@/lib/news-schema";
import { useDirtyStore } from "@/lib/dirty-store";
import { IssueRow } from "@/components/trial/IssueRow";
import { cn } from "@/lib/utils";
import { Info, Plus, Trash2, GripVertical, Pin } from "lucide-react";
import { ItemPicker } from "@/components/ItemPicker";
import { IapPicker } from "@/components/IapPicker";
import { HERO_NAMES, REWARD_TYPE_NAMES } from "@/types/cloud-data";

// ─── Draft shape: the editor's local working copy of one post ─────────────
// Deliberately flat (index-row fields + bodies together) — it is serialized
// back into a candidate index entry + post body on every keystroke, the same
// "one source of truth, pure derivation" approach trial.tsx uses for its
// JSON text.

interface Draft {
  id: string;
  titles: Partial<Record<NewsLanguage, string>>;
  bodies: Partial<Record<NewsLanguage, string>>;
  publishAt: string | null;
  expireAt: string | null;
  pinned: boolean;
  draft: boolean;
  order: number;
  rewards: MailReward[];
}

interface RewardTypeDef {
  hasAmount?: boolean;
  needsItem?: boolean;
  needsHero?: boolean;
  needsBuff?: boolean;
  needsRecipe?: boolean;
  needsIap?: boolean;
}
const NEWS_REWARD_TYPES: Record<number, RewardTypeDef> = {
  0: { hasAmount: true, needsItem: true },
  1: { hasAmount: true },
  2: { hasAmount: true },
  4: { needsHero: true },
  10: { hasAmount: true, needsBuff: true },
  11: { hasAmount: true },
  12: { hasAmount: true },
  14: { hasAmount: true },
  15: { hasAmount: true },
  16: { needsRecipe: true },
  100: { hasAmount: true, needsIap: true },
};

function blankDraft(order: number): Draft {
  return {
    id: "",
    titles: { en: "" },
    bodies: { en: "" },
    publishAt: null,
    expireAt: null,
    pinned: false,
    draft: true,
    order,
    rewards: [],
  };
}

function draftFromPost(post: NewsPostFull): Draft {
  return {
    id: post.id,
    titles: post.titles,
    bodies: post.bodies,
    publishAt: post.publishAt,
    expireAt: post.expireAt,
    pinned: post.pinned,
    draft: post.draft,
    order: post.order,
    rewards: post.rewards ?? [],
  };
}

function entryFromDraft(d: Draft): NewsIndexEntry {
  return {
    id: d.id,
    titles: d.titles,
    publishAt: d.publishAt,
    expireAt: d.expireAt,
    pinned: d.pinned,
    draft: d.draft,
    order: d.order,
  };
}

function postFromDraft(d: Draft): NewsPostFull {
  return {
    id: d.id,
    titles: d.titles,
    bodies: d.bodies,
    publishAt: d.publishAt,
    expireAt: d.expireAt,
    pinned: d.pinned,
    draft: d.draft,
    order: d.order,
    rewards: d.rewards,
  };
}

// ─── Lifecycle grouping — README's fixed display order ────────────────────

type Group = "pinned" | "published" | "scheduled" | "draft" | "expired";
const GROUP_ORDER: Group[] = ["pinned", "published", "scheduled", "draft", "expired"];
const GROUP_LABEL: Record<Group, string> = {
  pinned: "Pinned",
  published: "Published",
  scheduled: "Scheduled",
  draft: "Draft",
  expired: "Expired",
};

function lifecycleGroup(e: NewsIndexEntry, now: number): Group {
  if (e.pinned) return "pinned";
  if (e.draft) return "draft";
  const publishMs = e.publishAt ? Date.parse(e.publishAt) : NaN;
  const expireMs = e.expireAt ? Date.parse(e.expireAt) : NaN;
  if (!Number.isNaN(expireMs) && expireMs < now) return "expired";
  if (!Number.isNaN(publishMs) && publishMs > now) return "scheduled";
  return "published";
}

function localHint(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "invalid date";
  return `Your local time: ${d.toLocaleString()}`;
}

export function NewsRoute() {
  const qc = useQueryClient();
  const setDirtyGlobal = useDirtyStore((s) => s.setDirty);

  const listQuery = useQuery({
    queryKey: ["news"],
    queryFn: listNews,
  });

  const parsedIndex: NewsIndex = listQuery.data ?? [];

  const [selectedId, setSelectedId] = useState<string | "new" | null>(null);

  const selectedEntry = useMemo(
    () => (selectedId && selectedId !== "new" ? parsedIndex.find((e) => e.id === selectedId) ?? null : null),
    [selectedId, parsedIndex]
  );

  const postQuery = useQuery({
    queryKey: ["news", selectedEntry?.id],
    queryFn: () => getNewsPost(selectedEntry!.id),
    enabled: !!selectedEntry,
  });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [seededFor, setSeededFor] = useState<string | null>(null);

  // Seed the editor draft once we know which post is selected and (for an
  // existing post) its body has loaded.
  useEffect(() => {
    if (selectedId === "new") {
      if (seededFor !== "new") {
        const maxOrder = parsedIndex.reduce((m, e) => Math.max(m, e.order), -1);
        setDraft(blankDraft(maxOrder + 1));
        setSeededFor("new");
      }
      return;
    }
    if (selectedEntry && postQuery.isSuccess && postQuery.data) {
      if (seededFor !== selectedEntry.id) {
        setDraft(draftFromPost(postQuery.data));
        setSeededFor(selectedEntry.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selectedEntry, postQuery.isSuccess, postQuery.data]);

  // ── Candidate index + validation, recomputed on every draft change ──────
  const original = selectedEntry ?? null;
  const originalPost = postQuery.data ?? null;

  const { issues, dirty } = useMemo(() => {
    if (!draft) return { issues: [] as NewsIssue[], dirty: false };
    const entry = entryFromDraft(draft);
    const idx = parsedIndex.findIndex((e) => e.id === (original?.id ?? ""));
    const nextIndex = idx >= 0 ? parsedIndex.map((e, i) => (i === idx ? entry : e)) : [...parsedIndex, entry];
    const targetIdx = idx >= 0 ? idx : nextIndex.length - 1;

    const indexIssues = validateNewsIndex(nextIndex).filter(
      (i) => i.path === `[${targetIdx}]` || i.path.startsWith(`[${targetIdx}].`)
    );
    const bodyIssues = validateNewsPost({ id: draft.id, bodies: draft.bodies }, draft.id);
    const rewardIssues = validateNewsRewards(draft.rewards);

    const isDirty =
      selectedId === "new" ||
      JSON.stringify(entry) !== JSON.stringify(original) ||
      JSON.stringify(draft.bodies) !== JSON.stringify(originalPost?.bodies ?? {}) ||
      JSON.stringify(draft.rewards) !== JSON.stringify(originalPost?.rewards ?? []);

    return { issues: [...indexIssues, ...bodyIssues, ...rewardIssues], dirty: isDirty };
  }, [draft, parsedIndex, original, originalPost, selectedId]);

  const errors = issues.filter((i) => i.severity === "error");
  const warnings = issues.filter((i) => i.severity === "warning");
  const canSave = !!draft && dirty && errors.length === 0;

  useEffect(() => {
    setDirtyGlobal(dirty);
    return () => setDirtyGlobal(false);
  }, [dirty, setDirtyGlobal]);

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  function selectPost(id: string | "new" | null) {
    if (savingRef.current) return;
    if (dirty && !window.confirm("Discard unsaved changes to this post?")) return;
    saveMutation.reset();
    setSeededFor(null);
    setDraft(null);
    setSelectedId(id);
  }

  const savingRef = useRef(false);
  const isNew = selectedId === "new";

  const saveMutation = useMutation({
    mutationFn: async (post: NewsPostFull) => {
      await (isNew ? createNewsPost(post) : updateNewsPost(post.id, post));
      return post;
    },
    onSuccess: async (post) => {
      qc.setQueryData(["news", post.id], post);
      qc.setQueryData<NewsIndex>(["news"], (index = []) => {
        const entry = entryFromDraft(draftFromPost(post));
        return index.some((item) => item.id === post.id)
          ? index.map((item) => item.id === post.id ? entry : item)
          : [...index, entry];
      });
      setSelectedId(post.id);
      setSeededFor(post.id);
      setDraft(draftFromPost(post));
      await qc.invalidateQueries({ queryKey: ["news"] });
    },
    onSettled: () => { savingRef.current = false; },
  });

  function handleSave() {
    if (!draft || !canSave || savingRef.current || deleteMutation.isPending) return;
    savingRef.current = true;
    saveMutation.mutate(postFromDraft(draft));
  }

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteNewsPost(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["news"] });
      setSelectedId(null);
      setDraft(null);
      setSeededFor(null);
    },
  });

  function handleDelete() {
    if (!original || savingRef.current) return;
    const title = original.titles.en ?? original.id;
    if (!window.confirm(`Delete "${title}"? This removes the post from D1 and cannot be undone here.`)) return;
    deleteMutation.mutate(original.id);
  }

  // ── Reorder (drag within a lifecycle group) ─────────────────────────────
  const reorderMutation = useMutation({
    mutationFn: (order: Array<{ id: string; order: number }>) => reorderNews(order),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["news"] }),
  });

  const [dragId, setDragId] = useState<string | null>(null);
  function handleDropOn(targetId: string, group: Group, now: number) {
    if (savingRef.current || !dragId || dragId === targetId) return;
    const groupIds = parsedIndex
      .filter((e) => lifecycleGroup(e, now) === group)
      .sort((a, b) => a.order - b.order || Date.parse(b.publishAt ?? "0") - Date.parse(a.publishAt ?? "0"))
      .map((e) => e.id);
    const from = groupIds.indexOf(dragId);
    const to = groupIds.indexOf(targetId);
    if (from < 0 || to < 0) return;
    const reordered = [...groupIds];
    reordered.splice(from, 1);
    reordered.splice(to, 0, dragId);
    reorderMutation.mutate(reordered.map((id, i) => ({ id, order: i })));
    setDragId(null);
  }

  const now = Date.now();
  const grouped = useMemo(() => {
    const out: Record<Group, NewsIndexEntry[]> = { pinned: [], published: [], scheduled: [], draft: [], expired: [] };
    for (const e of parsedIndex) out[lifecycleGroup(e, now)].push(e);
    for (const g of GROUP_ORDER) {
      out[g].sort((a, b) => a.order - b.order || Date.parse(b.publishAt ?? "0") - Date.parse(a.publishAt ?? "0"));
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedIndex]);

  return (
    <AppShell title="News" subtitle="Editing news_post rows in D1">
      <div className="flex items-center gap-2 mb-3">
        <Button size="sm" onClick={() => selectPost("new")}>
          <Plus className="h-3.5 w-3.5" /> New post
        </Button>
        <div className="flex-1" />
        {listQuery.isFetching && <span className="text-xs text-muted-foreground">Refreshing…</span>}
      </div>

      {listQuery.isLoading && (
        <div className="space-y-2 max-w-3xl">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
      )}

      {listQuery.isError && (
        <Alert variant="destructive" className="max-w-2xl">
          <AlertDescription>
            Failed to load news from the Worker: {String(listQuery.error)}. This page cannot show or edit posts
            until the Worker's D1 binding is reachable.
          </AlertDescription>
        </Alert>
      )}

      {listQuery.isSuccess && (
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
          <div className="space-y-3 max-h-[80vh] overflow-y-auto pr-1">
            {parsedIndex.length === 0 && (
              <p className="text-xs text-muted-foreground">No news posts yet.</p>
            )}
            {GROUP_ORDER.map((g) =>
              grouped[g].length === 0 ? null : (
                <div key={g} className="space-y-1">
                  <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-1">
                    {GROUP_LABEL[g]}
                  </h3>
                  {grouped[g].map((e) => (
                    <div
                      key={e.id}
                      draggable={!saveMutation.isPending}
                      onDragStart={() => setDragId(e.id)}
                      onDragOver={(ev) => ev.preventDefault()}
                      onDrop={() => handleDropOn(e.id, g, now)}
                      onClick={() => selectPost(e.id)}
                      className={cn(
                        "flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs cursor-pointer transition-colors",
                        selectedId === e.id
                          ? "border-primary/60 bg-accent"
                          : "border-border hover:bg-accent/50"
                      )}
                    >
                      <GripVertical className="h-3.5 w-3.5 shrink-0 opacity-40" />
                      {e.pinned && <Pin className="h-3 w-3 shrink-0 text-primary" />}
                      <span className="truncate flex-1">{e.titles.en || <em className="opacity-60">(untitled)</em>}</span>
                      <span className="font-mono text-[10px] opacity-50 truncate max-w-[70px]">{e.id}</span>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>

          <div className="min-w-0">
            {!draft ? (
              <p className="text-sm text-muted-foreground">Select a post from the list, or create a new one.</p>
            ) : (
              <fieldset disabled={saveMutation.isPending || deleteMutation.isPending} className="min-w-0">
                <PostEditor
                  draft={draft}
                  setDraft={setDraft}
                  isNew={isNew}
                  issues={issues}
                  errors={errors}
                  warnings={warnings}
                  canSave={canSave}
                  onSave={handleSave}
                  saving={saveMutation.isPending}
                  onDelete={!isNew ? handleDelete : undefined}
                  deleting={deleteMutation.isPending}
                />
                {saveMutation.error && (
                  <Alert variant="destructive" className="mt-3">
                    <AlertDescription>{String(saveMutation.error)}</AlertDescription>
                  </Alert>
                )}
                {saveMutation.isSuccess && !dirty && (
                  <p role="status" className="mt-3 text-sm text-muted-foreground">Changes saved.</p>
                )}
              </fieldset>
            )}
          </div>
        </div>
      )}
    </AppShell>
  );
}

function PostEditor({
  draft,
  setDraft,
  isNew,
  issues,
  errors,
  warnings,
  canSave,
  onSave,
  saving,
  onDelete,
  deleting,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  isNew: boolean;
  issues: NewsIssue[];
  errors: NewsIssue[];
  warnings: NewsIssue[];
  canSave: boolean;
  onSave: () => void;
  saving: boolean;
  onDelete?: () => void;
  deleting: boolean;
}) {
  const [lang, setLang] = useState<NewsLanguage>("en");
  const idLocked = !isNew;

  function update(patch: Partial<Draft>) {
    setDraft({ ...draft, ...patch });
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4">
      <div className="space-y-4 min-w-0">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">
            id {idLocked && <span className="opacity-70">(locked — this is the read-state join key; changing it marks the post unread for every player)</span>}
          </label>
          <Input
            value={draft.id}
            disabled={idLocked}
            onChange={(e) => update({ id: e.target.value })}
            placeholder="2026-08-patch-1-9-0"
            className="font-mono"
          />
        </div>

        <div className="flex gap-1 bg-muted/30 rounded-lg p-1 w-fit">
          {NEWS_LANGUAGES.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => setLang(l)}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded transition-colors",
                lang === l ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              )}
            >
              {l.toUpperCase()}
              {l === "en" && <span className="ml-1 opacity-50">(required)</span>}
            </button>
          ))}
        </div>

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Title ({lang})</label>
          <Input
            value={draft.titles[lang] ?? ""}
            onChange={(e) => update({ titles: { ...draft.titles, [lang]: e.target.value } })}
          />
        </div>

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Body ({lang})</label>
          <textarea
            value={draft.bodies[lang] ?? ""}
            onChange={(e) => update({ bodies: { ...draft.bodies, [lang]: e.target.value } })}
            rows={10}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Publish at (UTC)</label>
            <DateTimePicker value={draft.publishAt} onChange={(publishAt) => update({ publishAt })} />
            <p className="text-[11px] text-muted-foreground">{localHint(draft.publishAt) || "No publish time — treated as always-published while not draft."}</p>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Expire at (UTC)</label>
            <DateTimePicker value={draft.expireAt} onChange={(expireAt) => update({ expireAt })} />
            <p className="text-[11px] text-muted-foreground">{localHint(draft.expireAt) || "No expiry — never expires."}</p>
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Status</label>
          <div className="flex gap-1 bg-muted/30 rounded-lg p-1 w-fit">
            {([true, false] as const).map((isDraft) => (
              <button
                key={String(isDraft)}
                type="button"
                onClick={() => update({ draft: isDraft })}
                className={cn(
                  "px-3 py-1.5 text-xs font-medium rounded transition-colors",
                  draft.draft === isDraft
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {isDraft ? "Draft" : "Published"}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">
            {draft.draft
              ? "Draft — never sent to players. The public endpoint does not list it and 404s on its id."
              : draft.publishAt && Date.parse(draft.publishAt) > Date.now()
                ? "Published — goes live to players at the publish time above."
                : "Published — live to players as soon as you save."}
          </p>
        </div>

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={draft.pinned} onChange={(e) => update({ pinned: e.target.checked })} />
            Pinned
          </label>
        </div>

        <div className="space-y-3">
          <label className="text-xs text-muted-foreground">
            Claimable rewards (optional — broadcast to every player who claims once)
          </label>
          {draft.rewards.map((r, i) => {
            const def = NEWS_REWARD_TYPES[r.rewardType] ?? {};
            return (
              <div key={i} className="flex gap-2 items-center flex-wrap">
                <select
                  value={r.rewardType}
                  onChange={(e) => {
                    const next = [...draft.rewards];
                    next[i] = {
                      rewardType: parseInt(e.target.value),
                      amount: 1,
                      itemID: undefined,
                      hero: undefined,
                      developerBuffID: undefined,
                      recipe: undefined, iapProductId: undefined,
                    };
                    update({ rewards: next });
                  }}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {Object.entries(REWARD_TYPE_NAMES).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                {def.hasAmount && (
                  <Input
                    type="number"
                    value={r.amount}
                    onChange={(e) => {
                      const next = [...draft.rewards];
                      next[i] = { ...r, amount: parseInt(e.target.value) || 0 };
                      update({ rewards: next });
                    }}
                    className="w-28"
                  />
                )}
                {def.needsIap && <IapPicker value={r.iapProductId ?? ""} onChange={(iapProductId) => {
                  const next = [...draft.rewards]; next[i] = { ...r, iapProductId }; update({ rewards: next });
                }} />}
                {def.needsItem && (
                  <ItemPicker
                    className="flex-1 min-w-[160px]"
                    value={r.itemID ?? ""}
                    onChange={(id) => {
                      const next = [...draft.rewards];
                      next[i] = { ...r, itemID: id };
                      update({ rewards: next });
                    }}
                  />
                )}
                {def.needsHero && (
                  <select
                    value={r.hero ?? ""}
                    onChange={(e) => {
                      const next = [...draft.rewards];
                      next[i] = { ...r, hero: parseInt(e.target.value) };
                      update({ rewards: next });
                    }}
                    className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="" disabled>Pick a hero</option>
                    {Object.entries(HERO_NAMES).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                )}
                {def.needsBuff && (
                  <Input
                    value={r.developerBuffID ?? ""}
                    onChange={(e) => {
                      const next = [...draft.rewards];
                      next[i] = { ...r, developerBuffID: e.target.value };
                      update({ rewards: next });
                    }}
                    placeholder="Buff ID"
                    className="flex-1"
                  />
                )}
                {def.needsRecipe && (
                  <Input
                    type="number"
                    value={r.recipe ?? ""}
                    onChange={(e) => {
                      const next = [...draft.rewards];
                      next[i] = { ...r, recipe: parseInt(e.target.value) || 0 };
                      update({ rewards: next });
                    }}
                    placeholder="Recipe ID"
                    className="w-28"
                  />
                )}
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => update({ rewards: draft.rewards.filter((_, j) => j !== i) })}
                >
                  ✕
                </Button>
              </div>
            );
          })}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => update({ rewards: [...draft.rewards, { rewardType: 2, amount: 100 }] })}
          >
            <Plus className="h-3.5 w-3.5" /> Add reward
          </Button>
        </div>

        <div>
          <h3 className="text-xs font-medium text-foreground/80 mb-1">Preview</h3>
          <div className="w-[360px] max-w-full rounded-md border border-border bg-muted/20 p-3">
            <div className="text-sm font-semibold mb-1">{draft.titles[lang] || draft.titles.en || "(no title)"}</div>
            <div className="text-xs whitespace-pre-wrap text-foreground/80">
              {draft.bodies[lang] || draft.bodies.en || "(no body)"}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2">
          {onDelete && (
            <Button variant="destructive" size="sm" onClick={onDelete} disabled={deleting}>
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </Button>
          )}
          <div className="flex-1" />
          <Badge variant={errors.length ? "destructive" : "secondary"}>
            {errors.length} error{errors.length === 1 ? "" : "s"}
          </Badge>
          <Badge variant="secondary">
            {warnings.length} warning{warnings.length === 1 ? "" : "s"}
          </Badge>
          <Button onClick={onSave} disabled={!canSave || saving || deleting}>
            {saving ? "Saving…" : draft.draft ? "Save draft" : "Save & publish"}
          </Button>
        </div>
      </div>

      <div className="space-y-2 min-w-0">
        <h2 className="text-sm font-medium text-foreground/80">Issues</h2>
        {issues.length === 0 && (
          <div className="flex items-start gap-2 rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <div>No issues found.</div>
          </div>
        )}
        <div className="space-y-1.5 max-h-[70vh] overflow-auto pr-1">
          {errors.map((issue, i) => (
            <IssueRow key={`e${i}`} issue={issue} />
          ))}
          {warnings.map((issue, i) => (
            <IssueRow key={`w${i}`} issue={issue} />
          ))}
        </div>
      </div>
    </div>
  );
}

// Re-exported so a future pattern check can import it without duplicating
// the id charset the id input silently constrains authors to.
export const NEWS_ID_HINT_PATTERN = NEWS_ID_PATTERN;
